/**
 * @module SecurityEventsContext
 * @description Contexto global para la gestión de eventos de seguridad según ISO 27001/27002.
 *
 * Implementa la gestión del ciclo de vida completo de incidentes de seguridad,
 * alineado con los controles ISO 27002:
 * - A.5.24: Planificación y preparación para respuesta a incidentes
 * - A.5.25: Evaluación y decisión sobre eventos de seguridad de la información
 * - A.5.26: Respuesta a incidentes de seguridad de la información
 * - A.5.27: Aprendizaje de los incidentes de seguridad de la información
 * - A.8.15-A.8.16: Gestión de registros (logging) y monitoreo
 *
 * Estrategia de datos: intenta cargar desde Supabase; si no hay datos o falla,
 * usa MOCK_EVENTS como datos de ejemplo para el modo demo.
 *
 * Funcionalidades clave:
 * - CRUD completo de eventos con actualización optimista
 * - Filtrado en tiempo real por categoría, severidad, estado y fecha
 * - Búsqueda de texto libre en título, descripción y responsables
 * - Métricas calculadas en tiempo real (resolución promedio, distribución por tipo)
 * - Historial de cambios de estado (registrado en Supabase via trigger)
 *
 * @version 1.0.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import type {
  SecurityEvent,
  SecurityEventStatusChange,
  SecurityEventFilter,
  SecurityEventMetrics,
  SecurityEventSeverity,
  SecurityEventStatus,
  SecurityEventCategory
} from '../types/iso';

// =====================================================
// INTERFACES Y TIPOS
// =====================================================

/**
 * Contrato del contexto de eventos de seguridad.
 *
 * @property events - Lista completa de eventos de la organización (sin filtrar).
 * @property filteredEvents - Subconjunto de eventos que pasan los filtros activos.
 * @property filter - Estado actual de los filtros aplicados.
 * @property searchQuery - Texto de búsqueda libre actual.
 * @property metrics - Métricas calculadas en tiempo real sobre los eventos.
 * @property loadEvents - Recarga eventos desde Supabase o mock.
 * @property createEvent - Crea un evento nuevo con actualización optimista.
 * @property updateEvent - Actualiza campos de un evento existente con reversión en error.
 * @property deleteEvent - Elimina un evento con reversión en error.
 * @property getEventById - Busca un evento por su UUID en el estado local.
 * @property loadStatusHistory - Carga el historial de cambios de estado desde Supabase.
 * @property changeEventStatus - Cambia el estado de un evento (el trigger de BD registra el historial).
 */
interface SecurityEventsContextType {
  events: SecurityEvent[];
  isLoading: boolean;
  error: string | null;
  filter: SecurityEventFilter;
  searchQuery: string;
  filteredEvents: SecurityEvent[];
  metrics: SecurityEventMetrics;
  clearError: () => void;
  refreshData: () => Promise<void>;
  setFilter: (filter: Partial<SecurityEventFilter>) => void;
  resetFilter: () => void;
  setSearchQuery: (query: string) => void;
  loadEvents: () => Promise<void>;
  createEvent: (event: Omit<SecurityEvent, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'statusHistory'>) => Promise<SecurityEvent>;
  updateEvent: (eventId: string, updates: Partial<SecurityEvent>) => Promise<void>;
  deleteEvent: (eventId: string) => Promise<void>;
  getEventById: (eventId: string) => SecurityEvent | undefined;
  loadStatusHistory: (eventId: string) => Promise<SecurityEventStatusChange[]>;
  changeEventStatus: (eventId: string, newStatus: SecurityEventStatus, notes?: string) => Promise<void>;
}

// =====================================================
// FILTRO INICIAL
// =====================================================

/**
 * Estado inicial del filtro — sin ningún criterio aplicado.
 * Se usa al resetear los filtros con `resetFilter()`.
 */
const initialFilter: SecurityEventFilter = {
  category: [],
  severity: [],
  status: [],
  assignedTo: [],
  reportedBy: [],
  dateRange: {
    start: '',
    end: ''
  }
};

// =====================================================
// MÉTRICAS INICIALES
// =====================================================

/**
 * Valores iniciales de métricas mientras se cargan los datos.
 * Evita que los componentes reciban `null` o `undefined` durante la carga.
 */
const initialMetrics: SecurityEventMetrics = {
  total: 0,
  open: 0,
  investigating: 0,
  contained: 0,
  resolved: 0,
  closed: 0,
  bySeverity: { critical: 0, high: 0, medium: 0, low: 0 },
  byCategory: {
    malware: 0,
    unauthorized_access: 0,
    data_breach: 0,
    phishing: 0,
    denial_of_service: 0,
    physical_security: 0,
    policy_violation: 0,
    configuration_change: 0,
    vulnerability: 0,
    other: 0
  },
  avgResolutionTimeHours: 0,
  eventsLast30Days: 0,
  eventsWithCorrectiveActions: 0
};

// =====================================================
// DATOS MOCK PARA DESARROLLO
// =====================================================

/**
 * Eventos de seguridad de ejemplo para el modo demo.
 * Representan escenarios reales comunes: acceso no autorizado, malware,
 * phishing, violación de políticas y fallo físico.
 * Se usan cuando Supabase no está disponible o la tabla está vacía.
 */
const MOCK_EVENTS: SecurityEvent[] = [
  {
    id: '1',
    organizationId: 'org-1',
    title: 'Intento de acceso no autorizado al servidor de producción',
    description: 'Se detectaron múltiples intentos de acceso fallidos desde IP externa 192.168.1.100',
    category: 'unauthorized_access',
    severity: 'high',
    status: 'investigating',
    reportedByName: 'Juan Pérez',
    reportedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    assignedToName: 'María García',
    affectedAssetIds: ['asset-1'],
    relatedControlIds: ['control-1'],
    correctiveActions: 'Se bloqueó la IP origen y se inició investigación forense.',
    actionsTakenByName: 'María García',
    actionsTakenAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: '2',
    organizationId: 'org-1',
    title: 'Detección de malware en estación de trabajo',
    description: 'Antivirus detectó troyano en equipo del área de finanzas',
    category: 'malware',
    severity: 'critical',
    status: 'contained',
    reportedByName: 'Carlos López',
    reportedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    assignedToName: 'Ana Martínez',
    affectedAssetIds: ['asset-2'],
    relatedControlIds: ['control-2'],
    correctiveActions: 'Equipo aislado de la red. Se ejecutó limpieza completa y se reinstalaron aplicaciones críticas.',
    actionsTakenByName: 'Ana Martínez',
    actionsTakenAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    resolution: 'Malware eliminado completamente. Se actualizaron firmas de antivirus en toda la red.',
    lessonsLearned: 'Implementar restricciones adicionales para descarga de archivos ejecutables.',
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: '3',
    organizationId: 'org-1',
    title: 'Campaña de phishing detectada',
    description: 'Múltiples usuarios reportaron correos sospechosos simulando ser del área de TI',
    category: 'phishing',
    severity: 'medium',
    status: 'resolved',
    reportedByName: 'Varios usuarios',
    reportedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    assignedToName: 'Pedro Sánchez',
    affectedAssetIds: [],
    relatedControlIds: ['control-3'],
    correctiveActions: 'Se envió alerta a todos los usuarios. Se bloqueó dominio malicioso en filtro de correo.',
    actionsTakenByName: 'Pedro Sánchez',
    actionsTakenAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    resolution: 'Ningún usuario comprometió credenciales. Se reforzó capacitación de concientización.',
    resolvedAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    lessonsLearned: 'Programar simulacros de phishing trimestrales para medir efectividad de capacitación.',
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: '4',
    organizationId: 'org-1',
    title: 'Violación de política de uso de USB',
    description: 'Usuario conectó dispositivo USB no autorizado en área restringida',
    category: 'policy_violation',
    severity: 'low',
    status: 'closed',
    reportedByName: 'Sistema DLP',
    reportedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    assignedToName: 'Laura Díaz',
    affectedAssetIds: ['asset-3'],
    relatedControlIds: ['control-4'],
    correctiveActions: 'Se conversó con el usuario y se reforzó política de dispositivos removibles.',
    actionsTakenByName: 'Laura Díaz',
    actionsTakenAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
    resolution: 'Usuario firmó compromiso de cumplimiento de políticas.',
    resolvedAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: '5',
    organizationId: 'org-1',
    title: 'Fallo en sistema de control de acceso físico',
    description: 'Puerta de datacenter no registró accesos durante 2 horas',
    category: 'physical_security',
    severity: 'high',
    status: 'open',
    reportedByName: 'Guardia de seguridad',
    reportedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    affectedAssetIds: ['asset-4'],
    relatedControlIds: ['control-5'],
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
  }
];

// =====================================================
// CONTEXTO
// =====================================================

/** Contexto interno — accesible solo mediante `useSecurityEvents()`. */
const SecurityEventsContext = createContext<SecurityEventsContextType | null>(null);

// =====================================================
// PROVIDER
// =====================================================

/**
 * Proveedor del contexto de eventos de seguridad.
 *
 * Gestiona el estado completo de eventos: carga desde Supabase con fallback a mock,
 * CRUD con actualización optimista y reversión en error, filtrado en tiempo real y
 * cálculo de métricas.
 *
 * @param children - Árbol de componentes React a envolver.
 */
export function SecurityEventsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilterState] = useState<SecurityEventFilter>(initialFilter);
  const [searchQuery, setSearchQuery] = useState('');

  // =====================================================
  // FUNCIONES DE UTILIDAD
  // =====================================================

  /** Limpia el error actual (útil para cerrar mensajes de error en la UI). */
  const clearError = useCallback(() => setError(null), []);

  /**
   * Actualiza uno o más campos del filtro activo sin resetear los demás.
   * @param newFilter - Campos del filtro a actualizar (merge parcial).
   */
  const setFilter = useCallback((newFilter: Partial<SecurityEventFilter>) => {
    setFilterState(prev => ({ ...prev, ...newFilter }));
  }, []);

  /** Resetea todos los filtros al estado inicial (sin ningún criterio). */
  const resetFilter = useCallback(() => {
    setFilterState(initialFilter);
  }, []);

  // =====================================================
  // TRANSFORMACIÓN DE DATOS DB -> APP
  // =====================================================

  /**
   * Convierte un registro de la tabla `security_events` (snake_case) al tipo
   * TypeScript `SecurityEvent` (camelCase) de la aplicación.
   *
   * Los campos `reportNumber`, `eventOrigin` y `externalTicketNumber` se almacenan
   * en la columna JSONB `metadata` de la BD, por lo que se extraen desde ahí.
   *
   * @param dbEvent - Registro crudo de Supabase con claves snake_case.
   * @returns Objeto `SecurityEvent` con claves camelCase listas para la UI.
   */
  const transformDbEventToApp = useCallback((dbEvent: Record<string, unknown>): SecurityEvent => {
    return {
      id: dbEvent.id as string,
      organizationId: dbEvent.organization_id as string,
      title: dbEvent.title as string,
      description: dbEvent.description as string | undefined,
      category: dbEvent.category as SecurityEventCategory,
      severity: dbEvent.severity as SecurityEventSeverity,
      status: dbEvent.status as SecurityEventStatus,
      reportedById: dbEvent.reported_by_id as string | undefined,
      reportedByName: dbEvent.reported_by_name as string | undefined,
      reportedAt: dbEvent.reported_at as string,
      assignedToId: dbEvent.assigned_to_id as string | undefined,
      assignedToName: dbEvent.assigned_to_name as string | undefined,
      affectedAssetIds: (dbEvent.affected_asset_ids as string[]) || [],
      relatedControlIds: (dbEvent.related_control_ids as string[]) || [],
      correctiveActions: dbEvent.corrective_actions as string | undefined,
      actionsTakenById: dbEvent.actions_taken_by_id as string | undefined,
      actionsTakenByName: dbEvent.actions_taken_by_name as string | undefined,
      actionsTakenAt: dbEvent.actions_taken_at as string | undefined,
      resolution: dbEvent.resolution as string | undefined,
      resolvedAt: dbEvent.resolved_at as string | undefined,
      resolvedById: dbEvent.resolved_by_id as string | undefined,
      lessonsLearned: dbEvent.lessons_learned as string | undefined,
      // Campos almacenados dentro del JSONB `metadata`
      reportNumber: (dbEvent.metadata as Record<string, unknown>)?.report_number as string | undefined,
      eventOrigin: (dbEvent.metadata as Record<string, unknown>)?.event_origin as string | undefined,
      externalTicketNumber: (dbEvent.metadata as Record<string, unknown>)?.external_ticket_number as string | undefined,
      metadata: dbEvent.metadata as Record<string, unknown> | undefined,
      createdAt: dbEvent.created_at as string,
      updatedAt: dbEvent.updated_at as string,
      createdBy: dbEvent.created_by as string | undefined,
      updatedBy: dbEvent.updated_by as string | undefined
    };
  }, []);

  // =====================================================
  // CARGA DE DATOS
  // =====================================================

  /**
   * Carga todos los eventos de seguridad de la organización desde Supabase,
   * ordenados por fecha de reporte (más recientes primero).
   *
   * Si no hay `organization_id` (no autenticado) o la BD falla, usa MOCK_EVENTS.
   * Si la BD está disponible pero sin datos, también usa MOCK_EVENTS para
   * que el modo demo funcione correctamente.
   */
  const loadEvents = useCallback(async () => {
    if (!user?.organization_id) {
      // Sin organización → mostrar datos mock
      setEvents(MOCK_EVENTS);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: queryError } = await supabase
        .from('security_events')
        .select('*')
        .eq('organization_id', user.organization_id)
        .order('reported_at', { ascending: false });

      if (queryError) throw queryError;

      if (data && data.length > 0) {
        const transformedEvents = data.map(transformDbEventToApp);
        setEvents(transformedEvents);
      } else {
        // Tabla vacía → usar datos de demostración
        setEvents(MOCK_EVENTS);
      }
    } catch (err) {
      console.error('[SECURITY_EVENTS] Error loading events:', err);
      // Fallback a datos mock para no dejar la UI vacía
      setEvents(MOCK_EVENTS);
      setError('Error al cargar eventos. Usando datos de demostración.');
    } finally {
      setIsLoading(false);
    }
  }, [user?.organization_id, transformDbEventToApp]);

  /** Alias de `loadEvents` para usar en botones de recarga manual. */
  const refreshData = useCallback(async () => {
    await loadEvents();
  }, [loadEvents]);

  /** Carga los eventos al montar el proveedor y al cambiar de organización. */
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // =====================================================
  // CRUD EVENTOS
  // =====================================================

  /**
   * Crea un nuevo evento de seguridad con actualización optimista.
   *
   * El evento se agrega al estado local inmediatamente con un UUID generado en cliente
   * (`crypto.randomUUID()`). Si la inserción en Supabase falla, se revierte el estado.
   *
   * En modo demo (sin `organization_id`), el evento solo persiste en el estado local.
   *
   * @param eventData - Datos del evento (sin id, timestamps ni organizationId).
   * @returns El evento completo con ID y timestamps asignados.
   * @throws Error si la inserción en Supabase falla (estado revertido antes de lanzar).
   */
  const createEvent = useCallback(async (
    eventData: Omit<SecurityEvent, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'statusHistory'>
  ): Promise<SecurityEvent> => {
    const newEvent: SecurityEvent = {
      ...eventData,
      id: crypto.randomUUID(),
      organizationId: user?.organization_id || 'org-1',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      statusHistory: []
    };

    // Actualización optimista: el evento aparece en la lista inmediatamente
    setEvents(prev => [newEvent, ...prev]);

    if (user?.organization_id) {
      try {
        const { error: insertError } = await supabase
          .from('security_events')
          .insert({
            id: newEvent.id,
            organization_id: newEvent.organizationId,
            title: newEvent.title,
            description: newEvent.description,
            category: newEvent.category,
            severity: newEvent.severity,
            status: newEvent.status,
            reported_by_id: newEvent.reportedById,
            reported_by_name: newEvent.reportedByName,
            reported_at: newEvent.reportedAt,
            assigned_to_id: newEvent.assignedToId,
            assigned_to_name: newEvent.assignedToName,
            affected_asset_ids: newEvent.affectedAssetIds,
            related_control_ids: newEvent.relatedControlIds,
            corrective_actions: newEvent.correctiveActions,
            actions_taken_by_id: newEvent.actionsTakenById,
            actions_taken_by_name: newEvent.actionsTakenByName,
            actions_taken_at: newEvent.actionsTakenAt,
            resolution: newEvent.resolution,
            lessons_learned: newEvent.lessonsLearned,
            metadata: {
              ...(newEvent.metadata || {}),
              report_number: newEvent.reportNumber,
              event_origin: newEvent.eventOrigin,
              external_ticket_number: newEvent.externalTicketNumber,
            },
            created_by: user.id
          });

        if (insertError) throw insertError;
      } catch (err) {
        console.error('[SECURITY_EVENTS] Error creating event:', err);
        // Revertir la actualización optimista si la BD falla
        setEvents(prev => prev.filter(e => e.id !== newEvent.id));
        throw err;
      }
    }

    return newEvent;
  }, [user]);

  /**
   * Actualiza campos de un evento existente con actualización optimista.
   *
   * Guarda el estado anterior de todos los eventos antes de actualizar,
   * para poder revertir exactamente en caso de error en Supabase.
   *
   * Mapea solo los campos definidos en `updates` para no sobreescribir
   * datos no modificados en la BD.
   *
   * @param eventId - UUID del evento a actualizar.
   * @param updates - Campos del evento a modificar (merge parcial).
   * @throws Error si la actualización en Supabase falla (estado revertido antes de lanzar).
   */
  const updateEvent = useCallback(async (eventId: string, updates: Partial<SecurityEvent>) => {
    // Guardar snapshot del estado actual para poder revertir si hay error
    const previousEvents = [...events];
    const eventIndex = events.findIndex(e => e.id === eventId);
    if (eventIndex === -1) return;

    // Actualización optimista
    setEvents(prev => prev.map(e =>
      e.id === eventId
        ? { ...e, ...updates, updatedAt: new Date().toISOString() }
        : e
    ));

    if (user?.organization_id) {
      try {
        // Construir objeto de actualización solo con los campos presentes en `updates`
        const dbUpdates: Record<string, unknown> = {
          updated_at: new Date().toISOString(),
          updated_by: user.id
        };

        // Mapear campos de app (camelCase) a BD (snake_case) — solo los definidos
        if (updates.title !== undefined) dbUpdates.title = updates.title;
        if (updates.description !== undefined) dbUpdates.description = updates.description;
        if (updates.category !== undefined) dbUpdates.category = updates.category;
        if (updates.severity !== undefined) dbUpdates.severity = updates.severity;
        if (updates.status !== undefined) dbUpdates.status = updates.status;
        if (updates.assignedToId !== undefined) dbUpdates.assigned_to_id = updates.assignedToId;
        if (updates.assignedToName !== undefined) dbUpdates.assigned_to_name = updates.assignedToName;
        if (updates.correctiveActions !== undefined) dbUpdates.corrective_actions = updates.correctiveActions;
        if (updates.actionsTakenById !== undefined) dbUpdates.actions_taken_by_id = updates.actionsTakenById;
        if (updates.actionsTakenByName !== undefined) dbUpdates.actions_taken_by_name = updates.actionsTakenByName;
        if (updates.actionsTakenAt !== undefined) dbUpdates.actions_taken_at = updates.actionsTakenAt;
        if (updates.resolution !== undefined) dbUpdates.resolution = updates.resolution;
        if (updates.resolvedAt !== undefined) dbUpdates.resolved_at = updates.resolvedAt;
        if (updates.lessonsLearned !== undefined) dbUpdates.lessons_learned = updates.lessonsLearned;
        if (updates.reportNumber !== undefined) {
          // Fusionar reportNumber dentro del JSONB metadata existente
          const currentMeta = (events.find(e => e.id === eventId)?.metadata || {}) as Record<string, unknown>;
          dbUpdates.metadata = { ...currentMeta, report_number: updates.reportNumber };
        }

        const { error: updateError } = await supabase
          .from('security_events')
          .update(dbUpdates)
          .eq('id', eventId);

        if (updateError) throw updateError;
      } catch (err) {
        console.error('[SECURITY_EVENTS] Error updating event:', err);
        // Revertir al snapshot anterior
        setEvents(previousEvents);
        throw err;
      }
    }
  }, [events, user]);

  /**
   * Elimina un evento de seguridad con actualización optimista.
   * Si la eliminación en Supabase falla, restaura el evento en el estado local.
   *
   * @param eventId - UUID del evento a eliminar.
   * @throws Error si la eliminación en Supabase falla (estado revertido antes de lanzar).
   */
  const deleteEvent = useCallback(async (eventId: string) => {
    const previousEvents = [...events];

    // Actualización optimista: remover inmediatamente de la lista
    setEvents(prev => prev.filter(e => e.id !== eventId));

    if (user?.organization_id) {
      try {
        const { error: deleteError } = await supabase
          .from('security_events')
          .delete()
          .eq('id', eventId);

        if (deleteError) throw deleteError;
      } catch (err) {
        console.error('[SECURITY_EVENTS] Error deleting event:', err);
        // Revertir restaurando el snapshot anterior
        setEvents(previousEvents);
        throw err;
      }
    }
  }, [events, user]);

  /**
   * Busca un evento en el estado local por su UUID.
   * No hace una consulta a la BD — usa los datos ya cargados en memoria.
   *
   * @param eventId - UUID del evento a buscar.
   * @returns El evento encontrado, o `undefined` si no existe.
   */
  const getEventById = useCallback((eventId: string): SecurityEvent | undefined => {
    return events.find(e => e.id === eventId);
  }, [events]);

  // =====================================================
  // HISTORIAL DE ESTADOS
  // =====================================================

  /**
   * Carga el historial de cambios de estado de un evento desde Supabase.
   * La tabla `security_event_status_history` es poblada automáticamente
   * por un trigger de PostgreSQL cuando `status` cambia en `security_events`.
   *
   * @param eventId - UUID del evento del cual obtener el historial.
   * @returns Lista de cambios de estado, del más reciente al más antiguo.
   */
  const loadStatusHistory = useCallback(async (eventId: string): Promise<SecurityEventStatusChange[]> => {
    if (!user?.organization_id) return [];

    try {
      const { data, error: queryError } = await supabase
        .from('security_event_status_history')
        .select('*')
        .eq('event_id', eventId)
        .order('changed_at', { ascending: false });

      if (queryError) throw queryError;

      return (data || []).map(item => ({
        id: item.id,
        eventId: item.event_id,
        fromStatus: item.from_status,
        toStatus: item.to_status,
        changedById: item.changed_by_id,
        changedByName: item.changed_by_name,
        changedAt: item.changed_at,
        notes: item.notes
      }));
    } catch (err) {
      console.error('[SECURITY_EVENTS] Error loading status history:', err);
      return [];
    }
  }, [user]);

  /**
   * Cambia el estado de un evento usando `updateEvent`.
   * El historial del cambio se registra automáticamente en Supabase
   * mediante un trigger en la tabla `security_events`.
   *
   * No hace nada si el evento ya está en el nuevo estado (evita cambios duplicados).
   *
   * @param eventId - UUID del evento a modificar.
   * @param newStatus - Nuevo estado del evento.
   * @param notes - Notas opcionales sobre el cambio (usadas por el trigger de BD).
   */
  const changeEventStatus = useCallback(async (
    eventId: string,
    newStatus: SecurityEventStatus,
    notes?: string
  ) => {
    const event = events.find(e => e.id === eventId);
    if (!event || event.status === newStatus) return;

    await updateEvent(eventId, { status: newStatus });

    // El historial se registra automáticamente por el trigger en la BD
  }, [events, updateEvent]);

  // =====================================================
  // FILTRADO DE EVENTOS
  // =====================================================

  /**
   * Lista de eventos filtrada en tiempo real según los criterios activos.
   * Se recalcula automáticamente cuando cambia `events`, `filter` o `searchQuery`.
   *
   * Criterios aplicados en orden:
   * 1. Búsqueda de texto libre (título, descripción, responsables).
   * 2. Filtro por categoría (OR entre categorías seleccionadas).
   * 3. Filtro por severidad (OR entre severidades seleccionadas).
   * 4. Filtro por estado (OR entre estados seleccionados).
   * 5. Filtro por rango de fechas (reportedAt entre start y end).
   */
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      // Filtro de búsqueda libre: coincide con título, descripción o responsables
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          event.title.toLowerCase().includes(query) ||
          event.description?.toLowerCase().includes(query) ||
          event.reportedByName?.toLowerCase().includes(query) ||
          event.assignedToName?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Filtro por categoría — OR: el evento debe estar en alguna de las seleccionadas
      if (filter.category.length > 0 && !filter.category.includes(event.category)) {
        return false;
      }

      // Filtro por severidad — OR: el evento debe tener alguna de las severidades seleccionadas
      if (filter.severity.length > 0 && !filter.severity.includes(event.severity)) {
        return false;
      }

      // Filtro por estado — OR: el evento debe estar en alguno de los estados seleccionados
      if (filter.status.length > 0 && !filter.status.includes(event.status)) {
        return false;
      }

      // Filtro por fecha de inicio — solo eventos reportados desde `start`
      if (filter.dateRange.start) {
        const startDate = new Date(filter.dateRange.start);
        const eventDate = new Date(event.reportedAt);
        if (eventDate < startDate) return false;
      }

      // Filtro por fecha de fin — solo eventos reportados hasta `end`
      if (filter.dateRange.end) {
        const endDate = new Date(filter.dateRange.end);
        const eventDate = new Date(event.reportedAt);
        if (eventDate > endDate) return false;
      }

      return true;
    });
  }, [events, filter, searchQuery]);

  // =====================================================
  // CÁLCULO DE MÉTRICAS
  // =====================================================

  /**
   * Métricas calculadas en tiempo real sobre todos los eventos (no los filtrados).
   * Se recalculan cada vez que cambia la lista completa de eventos.
   *
   * Incluye:
   * - Conteo por estado (open, investigating, contained, resolved, closed).
   * - Distribución por severidad y por categoría.
   * - Tiempo promedio de resolución (en horas) de eventos con `resolvedAt`.
   * - Cantidad de eventos en los últimos 30 días.
   * - Eventos con acciones correctivas documentadas.
   */
  const metrics = useMemo((): SecurityEventMetrics => {
    const total = events.length;
    const open = events.filter(e => e.status === 'open').length;
    const investigating = events.filter(e => e.status === 'investigating').length;
    const contained = events.filter(e => e.status === 'contained').length;
    const resolved = events.filter(e => e.status === 'resolved').length;
    const closed = events.filter(e => e.status === 'closed').length;

    const bySeverity: Record<SecurityEventSeverity, number> = {
      critical: events.filter(e => e.severity === 'critical').length,
      high: events.filter(e => e.severity === 'high').length,
      medium: events.filter(e => e.severity === 'medium').length,
      low: events.filter(e => e.severity === 'low').length
    };

    const byCategory: Record<SecurityEventCategory, number> = {
      malware: events.filter(e => e.category === 'malware').length,
      unauthorized_access: events.filter(e => e.category === 'unauthorized_access').length,
      data_breach: events.filter(e => e.category === 'data_breach').length,
      phishing: events.filter(e => e.category === 'phishing').length,
      denial_of_service: events.filter(e => e.category === 'denial_of_service').length,
      physical_security: events.filter(e => e.category === 'physical_security').length,
      policy_violation: events.filter(e => e.category === 'policy_violation').length,
      configuration_change: events.filter(e => e.category === 'configuration_change').length,
      vulnerability: events.filter(e => e.category === 'vulnerability').length,
      other: events.filter(e => e.category === 'other').length
    };

    // Tiempo promedio de resolución: diferencia entre reportedAt y resolvedAt en horas
    const resolvedEvents = events.filter(e => e.resolvedAt);
    let avgResolutionTimeHours = 0;
    if (resolvedEvents.length > 0) {
      const totalHours = resolvedEvents.reduce((sum, e) => {
        const reportedAt = new Date(e.reportedAt).getTime();
        const resolvedAt = new Date(e.resolvedAt!).getTime();
        return sum + (resolvedAt - reportedAt) / (1000 * 60 * 60);
      }, 0);
      avgResolutionTimeHours = Math.round(totalHours / resolvedEvents.length);
    }

    // Ventana de 30 días para detectar tendencias recientes
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const eventsLast30Days = events.filter(e =>
      new Date(e.reportedAt) >= thirtyDaysAgo
    ).length;

    // Eventos con acciones correctivas documentadas (texto no vacío)
    const eventsWithCorrectiveActions = events.filter(e =>
      e.correctiveActions && e.correctiveActions.trim() !== ''
    ).length;

    return {
      total,
      open,
      investigating,
      contained,
      resolved,
      closed,
      bySeverity,
      byCategory,
      avgResolutionTimeHours,
      eventsLast30Days,
      eventsWithCorrectiveActions
    };
  }, [events]);

  // =====================================================
  // VALOR DEL CONTEXTO
  // =====================================================

  const value: SecurityEventsContextType = {
    events,
    isLoading,
    error,
    filter,
    searchQuery,
    filteredEvents,
    metrics,
    clearError,
    refreshData,
    setFilter,
    resetFilter,
    setSearchQuery,
    loadEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    getEventById,
    loadStatusHistory,
    changeEventStatus
  };

  return (
    <SecurityEventsContext.Provider value={value}>
      {children}
    </SecurityEventsContext.Provider>
  );
}

// =====================================================
// HOOK
// =====================================================

/**
 * Hook para consumir el contexto de eventos de seguridad desde cualquier
 * componente hijo de `SecurityEventsProvider`.
 *
 * @throws Error si se usa fuera de `SecurityEventsProvider`.
 * @returns Estado completo y operaciones del contexto de eventos de seguridad.
 *
 * @example
 * const { filteredEvents, createEvent, metrics } = useSecurityEvents();
 * const criticalOpen = filteredEvents.filter(e => e.severity === 'critical' && e.status === 'open');
 */
export function useSecurityEvents(): SecurityEventsContextType {
  const context = useContext(SecurityEventsContext);
  if (!context) {
    throw new Error('useSecurityEvents must be used within a SecurityEventsProvider');
  }
  return context;
}

export default SecurityEventsContext;
